<script lang="ts">
	import { format } from 'svelte-i18n';
	//Design system components
	import { Text, Select, Stack } from '@Shelly/shelly-design-system';
	//Helpers
	import {
		is_gen_2,
		isTRV,
		isBluTRV,
		isCamera,
		isDiffuser,
		isShellyPresence,
		isDimmerWithTransitionMinLimitations,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { getLocalControlOptions } from 'src/helpers/actions/local/core/local_action_helpers';
	import { findDeviceByChannelMode } from 'src/helpers/actions/local/core/local_action_components_helpers';
	import { isLocalActionDeviceCalibrated } from 'src/helpers/actions/local/core/local_action_calibration_helpers';
	import { devices } from 'src/stores/lists/devices';
	import { isXT1ComponentDevice, isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
	import { saveLocalAction } from 'src/helpers/actions/local/core/local_action_save_helper';
	import { getMinBrightnessByDevice } from 'src/helpers/actions/webhooks/core/webhook_helpers_gen1';
	import { getLnmLocalActionMethod } from 'src/helpers/actions/local/core/local_action_lnm_helpers';
	import { normalizeCameraLocalActionMethod } from 'src/helpers/actions/local/devices/camera_local_actions';
	//Local Action Components
	import BrightnessLocalAction from './BrightnessLocalAction.svelte';
	import RgbwLocalAction from './RGBWLocalAction.svelte';
	import OnOffStateLocalAction from './OnOffStateLocalAction.svelte';
	import RollerPositionLocalAction from './RollerPositionLocalAction.svelte';
	import TargetComponentLocalAction from './TargetComponentLocalAction.svelte';
	import VirtualLocalActions from './VirtualLocalActions.svelte';
	import IrrigationLocalActions from './IrrigationLocalActions.svelte';
	import ControlTemperatureLocalAction from './ControlTemperatureLocalAction.svelte';
	import SetModeLocalAction from './SetModeLocalAction.svelte';
	import OnOffOutputLocalAction from './OnOffOutputLocalAction.svelte';
	import BluTRVLocalAction from './BLUTRVLocalAction.svelte';
	import TRVLocalAction from './TRVLocalAction.svelte';
	import CuryLocalAction from './CuryLocalAction.svelte';
	import MediaPlayerLocalActions from './MediaPlayerLocalActions.svelte';
	import PresenceLocalAction from './PresenceLocalAction.svelte';
	import CameraLocalAction from './CameraLocalAction.svelte';
	import LnmLocalAction from './LnmLocalAction.svelte';
	import {
		invalidTRVAction,
		invalidCoverAction,
		invalidCameraAction,
		invalidCuryAction,
		invalidLightsAction,
		invalidToggleAfterAction,
		invalidTransitionDurationAction,
		invalidLNMAction,
	} from 'src/helpers/actions/local/validation/local_actions_validation_helpers';
	//Types
	import type { Device } from 'src/types/Device';
	import type { ActionJob, LocalControlOptions } from 'src/types/requests/schedules/schedules_g2';

	export let device: Device;
	export let action: ActionJob;
	export let switchPeripheral = null;
	export let scheduleAction = false;
	export let disableSaveBtn = false;

	let selectedDevice = device;
	let controlOptions: LocalControlOptions[] = [];
	let controlOption: LocalControlOptions = null;
	let method = '';
	let errorInput = false;

	let transitionDuration = {
		min: isDimmerWithTransitionMinLimitations(device.type) ? 1 : 0.5,
		max: 10800,
		showError: false,
		disabled:
			device.type === 'SNDM-0013US' ||
			device.type === 'S3DM-0A1WW' ||
			(is_gen_2(device) ? 2 : 1) === 1,
	};

	let minBrightness = getMinBrightnessByDevice(device);
	let isCalibrated = false;

	const setControlOption = (selectedMethod: string) => {
		controlOption = controlOptions.find((i) => i.value === selectedMethod);
		if (!controlOption && controlOptions?.[0]) {
			controlOption = controlOptions[0];
			method = action.method = controlOption.value;
		}
	};

	const selectDevice = (targetDevice: Device, allDevices: Record<string, Device>) => {
		if (targetDevice === device && isXT1Module(device.type) && action?.method) {
			targetDevice =
				(Object.values(allDevices) as Device[]).find(
					(candidate) =>
						real_id(candidate.id) === real_id(device.id) &&
						isXT1ComponentDevice(candidate) &&
						findDeviceByChannelMode(candidate, action),
				) || device;
		}

		selectedDevice = targetDevice;
		controlOptions = getLocalControlOptions(
			action,
			selectedDevice,
			switchPeripheral,
			scheduleAction,
		);
		method = getLnmLocalActionMethod(action);
		if (isCamera(selectedDevice.type)) {
			method = normalizeCameraLocalActionMethod(method, selectedDevice);
		}
		setControlOption(method);
	};

	const changeTargetDevice = (targetDeviceId?: Device['id'], componentId?: number) => {
		const targetDevice = targetDeviceId ? $devices[targetDeviceId] : null;
		if (
			!isXT1Module(device?.type) ||
			!targetDevice ||
			componentId == null ||
			targetDevice.id === selectedDevice.id
		) {
			return;
		}
		action.method = '';
		action.params.id = componentId;
		selectDevice(targetDevice, $devices);
	};

	$: selectDevice(device, $devices);
	$: isCalibrated = isLocalActionDeviceCalibrated(selectedDevice);

	$: disableSaveBtn =
		!controlOption ||
		invalidLightsAction(method, controlOption) ||
		invalidCoverAction(isCalibrated, controlOption) ||
		invalidToggleAfterAction(controlOption) ||
		invalidTransitionDurationAction(transitionDuration, controlOption) ||
		invalidCuryAction(method, controlOption) ||
		invalidCameraAction(method, controlOption) ||
		invalidTRVAction(method, errorInput) ||
		invalidLNMAction(controlOption, errorInput);

	export function save() {
		return saveLocalAction(controlOption, method, transitionDuration);
	}
</script>

{#if controlOption && isCamera(device.type)}
	<CameraLocalAction bind:controlOption bind:method {controlOptions} {device} />
{:else if controlOption}
	<Stack gap="s">
		{#if (controlOption.attr?.target?.length ?? 0) > 1}
			<TargetComponentLocalAction
				bind:controlOption
				selectedDeviceId={selectedDevice.id}
				onChange={changeTargetDevice}
			/>
		{/if}

		{#if !isXT1Module(selectedDevice?.type) || isXT1ComponentDevice(selectedDevice)}
			<div>
				<Text class="mb-s" element="h2">
					{$format('schedule_select_local_action', {
						default: 'Select Local Action',
					})}:
				</Text>
				<Select
					size="m"
					value={controlOption}
					options={controlOptions}
					icon={null}
					on:change={(e) => {
						method = e.detail['value'];
						setControlOption(method);
					}}
					id="select-3agp4a3"
					aria-label={$format('schedule_select_local_action', {
						default: 'Select Local Action',
					})}
				/>
			</div>
		{/if}

		{#if controlOption.lnm}
			<LnmLocalAction bind:controlOption bind:errorInput />
		{/if}

		{#if controlOption.onOffState != undefined && !isDiffuser(device.type)}
			<OnOffStateLocalAction bind:controlOption />
		{:else if controlOption.attr?.output != undefined}
			<OnOffOutputLocalAction bind:controlOption />
		{/if}

		{#if isBluTRV(device.type)}
			<BluTRVLocalAction bind:controlOption bind:errorInput {device} />
		{:else if isTRV(device.type)}
			<TRVLocalAction bind:controlOption bind:errorInput {device} />
		{:else if isDiffuser(device.type)}
			<CuryLocalAction bind:controlOption />
		{:else if isShellyPresence(device.type)}
			<PresenceLocalAction bind:controlOption />
		{:else if device.category === 'media_player'}
			<MediaPlayerLocalActions bind:controlOption {device} />
		{:else if controlOption?.virtual}
			{#if device?.category === 'irrigation'}
				<IrrigationLocalActions bind:controlOption={controlOption.virtual} />
			{:else}
				<VirtualLocalActions bind:controlOption={controlOption.virtual} />
			{/if}
		{:else if controlOption.attr?.pos != undefined}
			<RollerPositionLocalAction bind:controlOption device={selectedDevice} />
		{:else if controlOption.attr?.brightness != undefined}
			<BrightnessLocalAction
				bind:controlOption
				bind:transitionDuration
				showOffsetProperty={is_gen_2(device)}
				{minBrightness}
			/>
			{#if controlOption.attr?.rgb != undefined}
				<RgbwLocalAction bind:controlOption />
			{/if}
		{/if}
		{#if controlOption.attr?.ct != undefined}
			<ControlTemperatureLocalAction bind:controlOption device={selectedDevice} />
		{/if}
		{#if controlOption.params?.config?.mode && controlOption.attr?.config}
			<SetModeLocalAction bind:controlOption />
		{/if}
	</Stack>
{/if}
